/**
 * Barcode scanner input.
 *
 * A USB or wireless scanner - the iTech IT-3000G included - is a keyboard as
 * far as the computer is concerned: it types the code and presses Enter. The
 * only thing that tells its keystrokes from a person's is speed. A scanner
 * types a 13-digit EAN a few milliseconds a character; nobody types a run of
 * characters at that pace and then hits Enter straight after.
 *
 * So this listens to the whole window for that pace, which is what lets a scan
 * work wherever focus happens to be:
 *
 *   On a button - the Enter would otherwise press it, and on the till the last
 *   tile tapped is still focused, so every scan would add that item again. The
 *   Enter is swallowed.
 *
 *   In a text box - the characters are already in the box by the time the
 *   burst is recognised, so the box is put back to what it held before. A
 *   barcode never lands in the Name field or the search box by accident.
 *
 * The scanner has to send Enter (its factory setting) or Tab after the code.
 */

import { useEffect, useRef } from 'react';

const TERMINATORS = new Set(['Enter', 'Tab']);

const NON_TEXT_INPUTS = new Set(['checkbox', 'radio', 'button', 'submit', 'reset', 'file', 'range', 'color']);

const isTextField = (el) =>
    el instanceof HTMLTextAreaElement
    || (el instanceof HTMLInputElement && !NON_TEXT_INPUTS.has(el.type));

/**
 * Set a React-controlled field's value, and tell React.
 *
 * Assigning `el.value` is invisible to React - it compares against the last
 * value it rendered and ignores the input event. Going through the prototype's
 * own setter is the way round that.
 */
const restoreValue = (el, value) => {
    if (el.value === value) return;
    const proto = el instanceof HTMLTextAreaElement ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype;
    Object.getOwnPropertyDescriptor(proto, 'value').set.call(el, value);
    el.dispatchEvent(new Event('input', { bubbles: true }));
};

/**
 * @param {(code: string, info: { target: EventTarget | null }) => void} onScan
 * @param {object} [options]
 * @param {boolean} [options.enabled=true]
 * @param {number}  [options.minLength=4]      shorter bursts are left alone
 * @param {number}  [options.maxAvgGapMs=50]   average time between characters
 * @param {number}  [options.maxGapMs=100]     any longer pause starts over
 */
export const useBarcodeScanner = (onScan, {
    enabled = true, minLength = 4, maxAvgGapMs = 50, maxGapMs = 100
} = {}) => {
    // Held in a ref, like useHotkeys, so the listener is not re-attached on
    // every render of a screen whose handler closes over changing state.
    const onScanRef = useRef(onScan);
    onScanRef.current = onScan;

    useEffect(() => {
        if (!enabled) return undefined;

        let buffer = '';
        let startedAt = 0;
        let lastAt = 0;
        let origin = null;   // the focused element, and a text field's value before the first character

        const reset = () => { buffer = ''; origin = null; };

        const onKeyDown = (event) => {
            if (event.isComposing || event.ctrlKey || event.altKey || event.metaKey) return;

            // A held-down key repeats at scanner speed; it is never a scan.
            if (event.repeat) { reset(); return; }

            const now = event.timeStamp;

            if (event.key.length === 1) {
                if (!buffer || now - lastAt > maxGapMs) {
                    buffer = '';
                    startedAt = now;
                    origin = {
                        target: event.target,
                        value: isTextField(event.target) ? event.target.value : null
                    };
                }
                buffer += event.key;
                lastAt = now;
                return;
            }

            // Capital letters arrive with a Shift press in between.
            if (event.key === 'Shift') return;

            const code = buffer.trim();
            const averageGap = buffer.length > 1 ? (lastAt - startedAt) / (buffer.length - 1) : Infinity;
            const isScan = TERMINATORS.has(event.key)
                && code.length >= minLength
                && now - lastAt <= maxGapMs
                && averageGap <= maxAvgGapMs;

            const { target = null, value = null } = origin || {};
            reset();

            if (!isScan) return;

            event.preventDefault();
            event.stopPropagation();
            if (value !== null && target?.isConnected) restoreValue(target, value);
            onScanRef.current?.(code, { target });
        };

        // Capture phase, so the Enter is stopped before any field, button or
        // React handler on the page sees it.
        window.addEventListener('keydown', onKeyDown, true);
        return () => window.removeEventListener('keydown', onKeyDown, true);
    }, [enabled, minLength, maxAvgGapMs, maxGapMs]);
};

export default useBarcodeScanner;
